import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, ModalBody } from "reactstrap";
import { RootState } from "../../reducers";
import { ObjectUtils, USER_TOKEN } from "../../utils";
import { extendSession, getIdleDeadline, isWarningEnabled, restoreSessionTimeOut } from "../../actions";
import { AuthenticationConstants } from "../../constants";
import { ButtonComponent } from "../../components/Button";

const formatCountdown = (totalSeconds: number): string => {
  const safe = totalSeconds > 0 ? totalSeconds : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const SessionTimeoutDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useSelector((state: RootState) => state.auth);
  const { contentFooter } = useSelector((state: RootState) => state.common);
  const content = contentFooter?.contentResource?.contents;

  const [remaining, setRemaining] = useState<number>(() => Math.max(0, Math.ceil((getIdleDeadline() - Date.now()) / 1000)));
  const [extending, setExtending] = useState<boolean>(false);
  const intervalRef = useRef<any>(null);
  const loggedOutRef = useRef<boolean>(false);

  const path = location.pathname?.toLowerCase() || "";
  const notAllowedPage = path.endsWith("/login") || path.endsWith("/logoff");
  const tokenState = USER_TOKEN.get();
  const signedIn = !!tokenState.isSignedIn || !!auth?.isSignedIn;
  // isActive drives the idle logout everywhere a session exists; shouldShow only decides whether the
  // dialog is drawn (pre-2FA the refresh endpoint rejects the token, so "Stay" must never be offered there)
  const isActive = !!auth?.showSessionTimeout && signedIn;
  const shouldShow = isActive && !notAllowedPage && !auth?.needTwoFAValidation;

  const handleLogout = useCallback(
    (isTimeout: boolean): void => {
      if (loggedOutRef.current) return;
      loggedOutRef.current = true;
      if (isTimeout) {
        localStorage.setItem("sessionTimeOut", "1");
      }
      dispatch({ type: AuthenticationConstants.SHOW_SESSION_TIMEOUT, payload: false });
      navigate("/logoff");
    },
    [dispatch, navigate]
  );

  const handleStay = useCallback((): void => {
    setExtending(true);
    extendSession().then((extended: boolean) => {
      setExtending(false);
      if (!extended) {
        handleLogout(true);
      }
    });
  }, [handleLogout]);

  // this dialog is mounted for the whole app lifetime, so its mount is the app-start
  // hook that re-arms session monitoring when a reload left the session signed in
  useEffect(() => {
    restoreSessionTimeOut();
  }, []);

  useEffect(() => {
    if (!isActive) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      loggedOutRef.current = false;
      return;
    }
    // remaining is derived from the idle deadline (never a private countdown),
    // so a late/throttled tick still shows the true remainder and logs out on schedule
    const tick = () => {
      const msLeft = getIdleDeadline() - Date.now();
      setRemaining(Math.max(0, Math.ceil(msLeft / 1000)));
      // while an extend call is in flight the click wins; 10s past expiry is the hard cap
      if (msLeft <= 0 && (!extending || msLeft <= -10000)) {
        handleLogout(true);
      }
    };
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isActive, extending, handleLogout]);

  // warning off, or a page where the dialog is suppressed: the tick effect above still performs
  // the silent idle logout, nothing is rendered
  if (!shouldShow || !isWarningEnabled()) {
    return null;
  }

  const title = ObjectUtils.extractLabelFromObject(content, "lblSessionTimeoutTitle");
  const message = ObjectUtils.extractLabelFromObject(content, "lblSessionTimeoutMessage");
  const stayLabel = ObjectUtils.extractLabelFromObject(content, "btnStaySignedIn");
  const logoutLabel = ObjectUtils.extractLabelFromObject(content, "btnLogoutNow");
  const announcement = ObjectUtils.extractLabelFromObject(content, "lblSessionTimeoutAnnouncement");

  const titleText = title?.display || "Session about to expire";
  const announcementText = announcement?.display || "Warning: your session is about to expire.";
  const stayText = stayLabel?.display || "Stay signed in";
  const logoutText = logoutLabel?.display || "Log out now";
  const messageTemplate = message?.display || "You've been inactive for a while, so we'll sign you out in {mm:ss} to keep your account secure. Would you like to stay signed in?";
  const messageText = messageTemplate.replace("{mm:ss}", formatCountdown(remaining));

  return (
    <Fragment>
      <Modal isOpen={shouldShow} aria-labelledby="sessionTimeoutHeading" aria-describedby="sessionTimeoutDescription" backdrop="static" keyboard={false} trapFocus centered>
        <div className="modal-header">
          <h1 className="modal-title" id="sessionTimeoutHeading">
            {titleText}
          </h1>
        </div>
        <ModalBody>
          <p className="visually-hidden" role="alert" aria-live="assertive">
            {announcementText}
          </p>
          <div className="ml-popupmessage" id="sessionTimeoutDescription">
            <p className="ml-popupmessagetext">{messageText}</p>
          </div>
          <div className="ml-buttons d-flex justify-content-between">
            <ButtonComponent
              disabled={extending}
              btndata={[
                {
                  variant: "outline-secondary btn-sm btn-cancel",
                  ariaLabel: logoutText,
                  display: logoutText,
                  onSubmit: () => handleLogout(false)
                }
              ]}
            />
            <ButtonComponent
              disabled={extending}
              btndata={[
                {
                  variant: "secondary btn-sm btn-submit",
                  ariaLabel: stayText,
                  display: stayText,
                  onSubmit: handleStay
                }
              ]}
            />
          </div>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default SessionTimeoutDialog;
